'use client'

import { useEffect, useState } from 'react'

import { usePaymentOrder } from '@/features/veluvit/hooks/use-payment-order'
import { isCheckoutInterrupted } from '@/features/veluvit/lib/payment-status'
import {
  closePaymentPopup,
  paymentPopupChannel,
  paymentReturnSchema,
} from '@/features/veluvit/services/payment-popup'
import { usePaymentPopupStore } from '@/features/veluvit/stores/payment-popup.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { PopupPayment } from '@/features/veluvit/stores/payment-popup.store'

function PopupMonitor({ payment }: { payment: PopupPayment }) {
  const [returned, setReturned] = useState(false)
  const { order, busy, error, walletError, refresh, check, refreshWallet } =
    usePaymentOrder(payment.ownerId, payment.order.id, {
      autoSync: false,
      pollLimit: 120,
      watchInterrupted: !returned,
    })
  const orderId = payment.order.id
  const settled =
    !!order &&
    (order.status !== 'pending' || (returned && isCheckoutInterrupted(order)))

  useEffect(() => {
    usePaymentPopupStore.getState().update(orderId, payment.id, {
      ...(order ? { order } : {}),
      busy,
      error,
      walletError,
      check,
      refreshWallet,
    })
  }, [
    orderId,
    payment.id,
    order,
    busy,
    error,
    walletError,
    check,
    refreshWallet,
  ])

  useEffect(() => {
    if (settled) {
      closePaymentPopup(payment.popup)
      usePaymentPopupStore
        .getState()
        .update(orderId, payment.id, { closed: true })
      return
    }
    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(paymentPopupChannel(payment.id))
      channel.onmessage = (event: MessageEvent<unknown>) => {
        const parsed = paymentReturnSchema.safeParse(event.data)
        if (
          parsed.success &&
          parsed.data.id === payment.id &&
          parsed.data.orderId === orderId
        ) {
          setReturned(true)
          void refresh()
        }
      }
    } catch {}
    let closed = false
    const timer = setInterval(() => {
      if (closed || !payment.popup.closed) return
      closed = true
      clearInterval(timer)
      usePaymentPopupStore
        .getState()
        .update(orderId, payment.id, { closed: true })
      void refresh()
    }, 1000)
    const focus = () => {
      void refresh()
    }
    window.addEventListener('focus', focus)
    return () => {
      clearInterval(timer)
      channel?.close()
      window.removeEventListener('focus', focus)
    }
  }, [orderId, payment.id, payment.popup, refresh, settled])

  return null
}

export default function PaymentPopupHost() {
  const payments = usePaymentPopupStore((state) => state.payments)
  const identity = useSessionStore((state) => state.identity)
  const ownerId = useSessionStore((state) =>
    state.data?.user?.guest ? undefined : state.data?.user?.id
  )

  useEffect(() => {
    for (const payment of Object.values(payments)) {
      if (payment.identity === identity && payment.ownerId === ownerId) continue
      closePaymentPopup(payment.popup)
      usePaymentPopupStore.getState().remove(payment.order.id, payment.id)
    }
  }, [payments, identity, ownerId])

  return (
    <>
      {Object.values(payments)
        .filter(
          (payment) =>
            payment.identity === identity && payment.ownerId === ownerId
        )
        .map((payment) => (
          <PopupMonitor key={payment.id} payment={payment} />
        ))}
    </>
  )
}
